// src/components/MailMarketingMap.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MailMarketingMap.css';

const MailMarketingMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Zip: ''
  });
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && !leafletMapRef.current) {
      // Initialize map centered on Austin, TX
      const map = L.map(mapRef.current).setView([30.2672, -97.7431], 12);

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Add click handler for map
      map.on('click', handleMapClick);

      leafletMapRef.current = map;
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Leaflet map is initialized once per mount

  useEffect(() => {
    const resizeMap = () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.invalidateSize();
      }
    };

    // Multiple resize attempts for mobile
    const timers = [
      setTimeout(resizeMap, 100),
      setTimeout(resizeMap, 300),
      setTimeout(resizeMap, 500)
    ];

    const handleResize = () => {
      if (leafletMapRef.current) {
        setTimeout(resizeMap, 100);
      }
    };

    const handleOrientationChange = () => {
      setTimeout(resizeMap, 200);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;
    setLoading(true);

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/reverse-geocode?latitude=${lat}&longitude=${lng}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const address = data.full_address;

        // Check if address already exists
        const addressExists = selectedAddresses.some(addr => addr.address === address);

        if (!addressExists) {
          const newAddress = {
            id: Date.now(),
            address,
            latitude: lat,
            longitude: lng,
            Name: "Valued Customer",
            CompanyName: "",
            Address1: address,
            Address2: "",
            City: "",
            State: "",
            Zip: ""
          };

          setSelectedAddresses(prev => [...prev, newAddress]);

          // Add purple circle marker to map
          L.circleMarker([lat, lng], {
            color: '#6f42c1',
            fillColor: '#6f42c1',
            fillOpacity: 0.7,
            radius: 8,
            weight: 2
          })
            .addTo(leafletMapRef.current)
            .bindPopup(address);
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeAddress = (id) => {
    setSelectedAddresses(prev => prev.filter(addr => addr.id !== id));

    // Remove marker from map
    leafletMapRef.current.eachLayer((layer) => {
      if (layer instanceof L.CircleMarker) {
        const pos = layer.getLatLng();
        const addressToRemove = selectedAddresses.find(addr => addr.id === id);
        if (addressToRemove &&
            Math.abs(pos.lat - addressToRemove.latitude) < 0.0001 &&
            Math.abs(pos.lng - addressToRemove.longitude) < 0.0001) {
          leafletMapRef.current.removeLayer(layer);
        }
      }
    });
  };

  const clearAll = () => {
    setSelectedAddresses([]);

    // Remove all markers
    leafletMapRef.current.eachLayer((layer) => {
      if (layer instanceof L.CircleMarker) {
        leafletMapRef.current.removeLayer(layer);
      }
    });
  };

  const handleAddAddress = async () => {
    setVerifying(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/verify-addresses', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          addresses: [newAddress],
          includeDetails: true,
          properCase: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.results?.data?.results && data.results.data.results.length > 0) {
          const verified = data.results.data.results[0].verifiedAddress;
          const fullAddress = `${verified.line1}, ${verified.city}, ${verified.provinceOrState} ${verified.postalOrZip}`;

          const addressToAdd = {
            id: Date.now(),
            address: fullAddress,
            latitude: 0,
            longitude: 0,
            Name: "Valued Customer",
            CompanyName: "",
            Address1: verified.line1,
            Address2: "",
            City: verified.city,
            State: verified.provinceOrState,
            Zip: verified.postalOrZip
          };

          setSelectedAddresses(prev => [...prev, addressToAdd]);
          setShowAddModal(false);
          setNewAddress({ Address1: '', Address2: '', City: '', State: '', Zip: '' });
        } else {
          alert('Address could not be verified. Please check the details.');
        }
      } else {
        alert('Error verifying address. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying address:', error);
      alert('Error verifying address. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="mail-marketing-map">
      <div className="map-header">
        <h1>Select Addresses from Map</h1>
        <button onClick={() => navigate('/admin/mail-marketing')} className="back-button">
          Back to Mail Marketing
        </button>
      </div>

      <div className="map-container">
        <div className="map-section">
          <div className="map-instructions">
            <p>Click on the map to select addresses for your mail campaign</p>
            {loading && <div className="loading-indicator">Getting address...</div>}
          </div>
          <div ref={mapRef} className="leaflet-map"></div>
        </div>

        <div className="addresses-panel">
          <div className="panel-header">
            <h3>Selected Addresses ({selectedAddresses.length})</h3>
            <div style={{display: 'flex', gap: '10px'}}>
              <button onClick={() => setShowAddModal(true)} className="add-address-button">
                Add Address
              </button>
              {selectedAddresses.length > 0 && (
                <button onClick={clearAll} className="clear-all-button">
                  Clear All
                </button>
              )}
            </div>
          </div>

          <div className="addresses-list">
            {selectedAddresses.length === 0 ? (
              <div className="empty-state">
                <p>No addresses selected yet</p>
                <p>Click on the map to add addresses</p>
              </div>
            ) : (
              selectedAddresses.map((addr) => (
                <div key={addr.id} className="address-item">
                  <div className="address-text">{addr.address}</div>
                  <button
                    onClick={() => removeAddress(addr.id)}
                    className="remove-button"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          {selectedAddresses.length > 0 && (
            <div className="panel-footer">
              <button
                className="continue-button"
                onClick={() => navigate('/admin/mail-marketing/upload', { state: { addresses: selectedAddresses } })}
              >
                Continue with {selectedAddresses.length} address{selectedAddresses.length !== 1 ? 'es' : ''}
              </button>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Address</h3>
              <button onClick={() => setShowAddModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  value={newAddress.Address1}
                  onChange={(e) => setNewAddress({...newAddress, Address1: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={newAddress.Address2}
                  onChange={(e) => setNewAddress({...newAddress, Address2: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>City *</label>
                <input
                  type="text"
                  value={newAddress.City}
                  onChange={(e) => setNewAddress({...newAddress, City: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>State *</label>
                  <input
                    type="text"
                    value={newAddress.State}
                    onChange={(e) => setNewAddress({...newAddress, State: e.target.value})}
                    maxLength="2"
                  />
                </div>
                <div className="form-field">
                  <label>Zip Code *</label>
                  <input
                    type="text"
                    value={newAddress.Zip}
                    onChange={(e) => setNewAddress({...newAddress, Zip: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAddModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleAddAddress}
                disabled={verifying || !newAddress.Address1 || !newAddress.City || !newAddress.State || !newAddress.Zip}
                className="submit-btn"
              >
                {verifying ? 'Verifying...' : 'Add Address'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailMarketingMap;
